import React, { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Inbox, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";

import { Skeleton } from "../../../../../components/ui/skeleton";
import { EDSButton } from "../../../../../components/eds/EDSButton";
import {
  Enquiry,
  EnquiryStatus,
  FilterOptions
} from "../../../../../types/enquiries";
import { EnquiryCard } from "../EnquiryCard";
import { STATUS_CONFIG } from "../constants";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";

export interface EnquiriesMainProps {
  enquiries: Enquiry[];
  loading: boolean;
  error: string | null;
  filters: FilterOptions;
  total: number;
  offset: number;
  limit: number;
  recentlyUpdatedIds: string[];
  takeEnquiry: (id: string) => void;
  updateStatus: (id: string, status: EnquiryStatus) => void;
  refetch: () => void;
  onPaginate: (offset: number) => void;
  onStatusFilterClick: (status: EnquiryStatus) => void;
  onClearFilters: () => void;
}

export const EnquiriesMain: React.FC<EnquiriesMainProps> = ({
  enquiries,
  loading,
  error,
  filters,
  total,
  offset,
  limit,
  recentlyUpdatedIds,
  takeEnquiry,
  updateStatus,
  refetch,
  onPaginate,
  onStatusFilterClick,
  onClearFilters
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setDiaryContext } = useDiaryContext();

  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  const handleViewEnquiry = useCallback(
    (enquiry: Enquiry) => {
      setDiaryContext({
        selectedEnquiry: enquiry,
        enquiriesReturnSearch: searchRef.current
      });
      navigate(`/bookings/${enquiry._id}`, { state: { fromEnquiries: true } });
    },
    [navigate, setDiaryContext]
  );

  const handlePrevPage = useCallback(() => {
    if (offset > 0) {
      onPaginate(Math.max(0, offset - limit));
    }
  }, [offset, limit, onPaginate]);

  const handleNextPage = useCallback(() => {
    if (offset + limit < total) {
      onPaginate(offset + limit);
    }
  }, [offset, limit, total, onPaginate]);

  const startItem = total > 0 ? offset + 1 : 0;
  const endItem = Math.min(offset + limit, total);

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
  ).length;

  const renderEnquiriesContent = (): React.ReactNode => {
    if (loading) {
      return (
        <div aria-label="Loading enquiries">
          {Array.from({ length: limit }, (_, i) => (
            <div
              key={`skeleton-${i}`}
              className="relative flex items-center gap-3 p-3 pl-14 pr-16 border-b border-border bg-card"
            >
              <Skeleton className="absolute left-3 h-8 w-8 rounded-full" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-3.5 w-10" />
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3.5 w-14" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
          <AlertCircle size={48} className="text-destructive" />
          <h3 className="text-lg font-semibold">Failed to load enquiries</h3>
          <p className="text-muted-foreground max-w-md">{error}</p>
          <EDSButton onClick={() => refetch()}>Try again</EDSButton>
        </div>
      );
    }

    if (enquiries.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
          <Inbox size={64} className="text-muted-foreground/50" />
          <h3 className="text-lg font-semibold">No enquiries found</h3>
          <p className="text-muted-foreground max-w-md">
            {activeFilterCount > 0
              ? "Try adjusting your filters to see more results."
              : "There are no enquiries to display at the moment."}
          </p>
        </div>
      );
    }

    return (
      <div>
        {enquiries.map((enquiry) => (
          <EnquiryCard
            key={enquiry._id}
            enquiry={enquiry}
            isRecentlyUpdated={recentlyUpdatedIds.includes(enquiry._id)}
            onTake={() => takeEnquiry(enquiry._id)}
            onStatusChange={(status) => updateStatus(enquiry._id, status)}
            onView={() => handleViewEnquiry(enquiry)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground">
            Showing {startItem}-{endItem} of {total} enquiries
          </span>
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {filters.status?.map((status) => (
                <button
                  key={status}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent text-accent-foreground text-sm hover:bg-accent/80 transition-colors"
                  onClick={() => onStatusFilterClick(status)}
                  aria-label={`Remove ${STATUS_CONFIG[status].label} filter`}
                >
                  {STATUS_CONFIG[status].label}
                  <X size={14} />
                </button>
              ))}
              <button
                className="flex items-center gap-1 px-2 py-1 rounded-full bg-destructive/10 text-destructive text-sm hover:bg-destructive/20 transition-colors"
                onClick={onClearFilters}
                aria-label="Clear all filters"
              >
                Clear all
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 relative min-h-0">
        <div className="absolute inset-0 overflow-y-auto">
          {renderEnquiriesContent()}
        </div>
      </div>

      {!loading && enquiries.length > 0 && (
        <div className="flex items-center justify-between px-4 pt-3 pb-6 border-t border-border bg-card">
          <EDSButton
            variant="secondary"
            onClick={handlePrevPage}
            disabled={offset === 0}
          >
            <ChevronLeft size={16} />
            Previous
          </EDSButton>
          <span className="text-sm text-muted-foreground">
            Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
          </span>
          <EDSButton
            variant="secondary"
            onClick={handleNextPage}
            disabled={offset + limit >= total}
          >
            Next
            <ChevronRight size={16} />
          </EDSButton>
        </div>
      )}
    </div>
  );
};
